import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import { createDepartmentAssignment, getMemberDetail, updateDepartmentAssignment, type DepartmentAssignment, type DepartmentCode } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";
import { TextField } from "../components/TextField";

const departments: Record<DepartmentCode, string> = {
  DAF: "Administrativo e Financeiro",
  DAE: "Assistência Espiritual",
  DEIJ: "Evangelização da Infância e Juventude",
  DAPS: "Assistência e Promoção Social",
  DAFA: "Assuntos da Família",
  DE: "Educação",
};

type AssignmentView =
  | { type: "loading" }
  | { type: "ready"; assignment: DepartmentAssignment }
  | { type: "error"; message: string };

function departmentLabel(department: DepartmentCode): string {
  return `${department} — ${departments[department]}`;
}

interface DepartmentFieldsProps {
  department: DepartmentCode;
  activity: string;
  role: string;
  startedAt: string;
  endedAt: string;
  canWrite: boolean;
  onDepartmentChange: (department: DepartmentCode) => void;
  onActivityChange: (activity: string) => void;
  onRoleChange: (role: string) => void;
  onStartedAtChange: (startedAt: string) => void;
  onEndedAtChange: (endedAt: string) => void;
}

function CreateDepartmentFields({ department, activity, role, startedAt, canWrite, onDepartmentChange, onActivityChange, onRoleChange, onStartedAtChange }: DepartmentFieldsProps) {
  return <>
    <label className="select-field"><span>Departamento</span><select value={department} onChange={(event) => onDepartmentChange(event.target.value as DepartmentCode)} disabled={!canWrite}>{Object.keys(departments).map((item) => <option key={item} value={item}>{departmentLabel(item as DepartmentCode)}</option>)}</select></label>
    <TextField label="Atividade" required disabled={!canWrite} value={activity} onChange={(event) => onActivityChange(event.target.value)} />
    <TextField label="Função" required disabled={!canWrite} value={role} onChange={(event) => onRoleChange(event.target.value)} />
    <TextField label="Início" required type="date" disabled={!canWrite} value={startedAt} onChange={(event) => onStartedAtChange(event.target.value)} />
  </>;
}

function EditDepartmentFields({ department, activity, role, startedAt, endedAt, canWrite, onActivityChange, onRoleChange, onEndedAtChange }: DepartmentFieldsProps) {
  return <>
    <TextField label="Departamento" disabled value={departmentLabel(department)} readOnly />
    <TextField label="Início" type="date" disabled value={startedAt} readOnly />
    <TextField label="Atividade" required disabled={!canWrite} value={activity} onChange={(event) => onActivityChange(event.target.value)} />
    <TextField label="Função" required disabled={!canWrite} value={role} onChange={(event) => onRoleChange(event.target.value)} />
    <TextField label="Encerramento" type="date" hint="Deixe em branco para manter o vínculo atual." disabled={!canWrite} min={startedAt} value={endedAt} onChange={(event) => onEndedAtChange(event.target.value)} />
  </>;
}

function validationMessage(activity: string, role: string, startedAt: string, endedAt: string, isEditing: boolean): string | null {
  if (!activity.trim() || !role.trim() || (!isEditing && !startedAt)) return "Preencha atividade, função e data de início.";
  if (isEditing && endedAt && endedAt < startedAt) return "A data de encerramento não pode ser anterior à data de início.";
  return null;
}

function saveAssignment(accessToken: string, memberId: string, assignmentId: string | undefined, department: DepartmentCode, activity: string, role: string, startedAt: string, endedAt: string) {
  if (assignmentId) return updateDepartmentAssignment(accessToken, assignmentId, { activity: activity.trim(), role: role.trim(), ended_at: endedAt || null });
  return createDepartmentAssignment(accessToken, memberId, { department, activity: activity.trim(), role: role.trim(), started_at: startedAt });
}

function saveErrorMessage(cause: unknown): string {
  return cause instanceof ApiError || cause instanceof NetworkError ? cause.message : "Não foi possível salvar o vínculo.";
}

async function loadAssignment(accessToken: string, memberId: string, assignmentId: string): Promise<AssignmentView> {
  const profile = await getMemberDetail(accessToken, memberId);
  if (profile.id !== memberId) return { type: "error", message: "Vínculo de Departamento não pertence a este associado." };
  const assignment = profile.department_assignments.find((item) => item.id === assignmentId);
  if (!assignment) return { type: "error", message: "Vínculo de Departamento não encontrado para este associado." };
  return { type: "ready", assignment };
}

function useAssignmentHydration(memberId: string | undefined, assignmentId: string | undefined, accessToken: string | undefined, expireSession: () => void, onAssignment: (assignment: DepartmentAssignment) => void) {
  const [view, setView] = useState<AssignmentView>({ type: "loading" });

  useEffect(() => {
    if (!memberId || !assignmentId || !accessToken) return;
    let cancelled = false;
    void loadAssignment(accessToken, memberId, assignmentId)
      .then((nextView) => {
        if (cancelled) return;
        if (nextView.type === "ready") onAssignment(nextView.assignment);
        setView(nextView);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) { expireSession(); return; }
        setView({ type: "error", message: cause instanceof ApiError || cause instanceof NetworkError ? cause.message : "Não foi possível carregar o vínculo." });
      });
    return () => { cancelled = true; };
  }, [accessToken, assignmentId, expireSession, memberId, onAssignment]);

  return view;
}

interface DepartmentFormPageProps {
  memberId: string;
  fields: DepartmentFieldsProps;
  isEditing: boolean;
  canWrite: boolean;
  message: { tone: "error" | "success"; text: string } | null;
  saving: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

function DepartmentFormPage({ memberId, fields, isEditing, canWrite, message, saving, onSubmit }: DepartmentFormPageProps) {
  const title = isEditing ? "Atualizar Departamento" : "Novo vínculo de Departamento";
  const buttonLabel = isEditing ? "Salvar vínculo" : "Criar vínculo";
  const Fields = isEditing ? EditDepartmentFields : CreateDepartmentFields;

  return (
    <main className="operational-page">
      <Link className="page-back" to={`/associados/${memberId}`}>Voltar para ficha</Link>
      <section className="form-card" aria-labelledby="department-title">
        <p className="members-page__eyebrow">Secretaria e DAF</p>
        <h1 id="department-title">{title}</h1>
        <p className="form-card__intro">A atribuição funcional é independente da categoria do associado e do papel de acesso ao sistema.</p>
        {!canWrite && <StatusBanner tone="warning">Seu acesso não possui permissão para alterar Departamentos.</StatusBanner>}
        {message && <StatusBanner tone={message.tone}>{message.text}</StatusBanner>}
        <form onSubmit={onSubmit} noValidate>
          <div className="form-grid"><Fields {...fields} /></div>
          {canWrite && <div className="form-actions"><Button type="submit" loading={saving}>{buttonLabel}</Button></div>}
        </form>
      </section>
    </main>
  );
}

export function MemberDepartmentPage() {
  const { memberId, assignmentId } = useParams();
  const { state, expireSession } = useAuth();
  const [department, setDepartment] = useState<DepartmentCode>("DAF");
  const [activity, setActivity] = useState("");
  const [role, setRole] = useState("");
  const [startedAt, setStartedAt] = useState(new Date().toISOString().slice(0, 10));
  const [endedAt, setEndedAt] = useState("");
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const hydrate = useCallback((assignment: DepartmentAssignment) => {
    setDepartment(assignment.department as DepartmentCode);
    setActivity(assignment.activity ?? "");
    setRole(assignment.role ?? "");
    setStartedAt(assignment.started_at);
    setEndedAt(assignment.ended_at ?? "");
  }, []);
  const view = useAssignmentHydration(
    memberId,
    assignmentId,
    state.status === "authenticated" ? state.accessToken : undefined,
    expireSession,
    hydrate,
  );

  if (state.status !== "authenticated" || !memberId) return null;
  const accessToken = state.accessToken;
  const currentMemberId = memberId;
  const canWrite = Boolean(state.sessionContext?.capabilities.write_departments);
  const isEditing = Boolean(assignmentId);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setMessage(null);
    const error = validationMessage(activity, role, startedAt, endedAt, isEditing);
    if (error) { setMessage({ tone: "error", text: error }); return; }
    setSaving(true);
    try {
      await saveAssignment(accessToken, currentMemberId, assignmentId, department, activity, role, startedAt, endedAt);
      setMessage({ tone: "success", text: isEditing ? "Vínculo atualizado." : "Vínculo de Departamento criado." });
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) { expireSession(); return; }
      setMessage({ tone: "error", text: saveErrorMessage(cause) });
    } finally { setSaving(false); }
  }

  if (isEditing && view.type === "loading") {
    return <main className="operational-page"><p className="members-state">Carregando vínculo…</p></main>;
  }

  if (isEditing && view.type === "error") {
    return <main className="operational-page"><Link className="page-back" to={`/associados/${memberId}`}>Voltar para ficha</Link><StatusBanner tone="error">{view.message}</StatusBanner></main>;
  }

  const fields: DepartmentFieldsProps = {
    department, activity, role, startedAt, endedAt, canWrite,
    onDepartmentChange: setDepartment, onActivityChange: setActivity, onRoleChange: setRole,
    onStartedAtChange: setStartedAt, onEndedAtChange: setEndedAt,
  };
  return <DepartmentFormPage memberId={memberId} fields={fields} isEditing={isEditing} canWrite={canWrite} message={message} saving={saving} onSubmit={submit} />;
}
